import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ServerHost } from "./lib/api";
import { CHATS, INITIAL_CONVERSATION } from "./data";
import type {
  AddHostPayload, ChatMessage, ChatSummary, Host, ThemeMode,
} from "./types";

export const chatKey = (hostId: string, chatId: string) => `${hostId}/${chatId}`;

// Maps the API's host shape to the local UI shape. Metrics start at 0 because
// the API only owns config; live telemetry comes from a separate channel.
const serverToLocalHost = (h: ServerHost): Host => ({
  id: h.id,
  name: h.name,
  addr: `${h.sshUser}@${h.hostname}`,
  region: "—",
  tag: h.tags[0] ?? "new",
  os: h.os ?? "linux",
  connectionMode: h.connectionMode ?? "direct",
  status: "online",
  latency: 0,
  cpu: 0,
  mem: 0,
  disk: 0,
  lastSeen: "now",
  group: "",
  spark: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  note: h.note ?? undefined,
});

interface AppStore {
  // ─── state ──────────────────────────────────────────────────────────────
  hosts: Host[];
  hostsLoading: boolean;
  chatsByHost: Record<string, ChatSummary[]>;
  messagesByChat: Record<string, ChatMessage[]>;
  theme: ThemeMode;
  accent: string;
  navCollapsed: boolean;
  /** Which panel the mobile (bottom-nav) layout shows. Ephemeral, not persisted. */
  mobileView: "servers" | "terminal";

  // ─── actions ────────────────────────────────────────────────────────────
  setTheme: (t: ThemeMode) => void;
  setAccent: (c: string) => void;
  toggleNav: () => void;
  setNavCollapsed: (collapsed: boolean) => void;
  setMobileView: (view: "servers" | "terminal") => void;
  addHost: (payload: AddHostPayload) => string;
  addServerHost: (host: ServerHost) => void;
  setHosts: (hosts: ServerHost[]) => void;
  setHostsLoading: (loading: boolean) => void;
  updateHost: (id: string, patch: Partial<Host>) => void;
  removeHost: (id: string) => void;
  newChat: (hostId: string) => string;
  approveTool: (chatKey: string, msgId: string, toolId: string) => void;
  denyTool: (chatKey: string, msgId: string, toolId: string) => void;
  sendMessage: (chatKey: string, text: string) => void;
}

export const useAppStore = create<AppStore>()(persist((set) => ({
  hosts: [],
  hostsLoading: true,
  chatsByHost: CHATS,
  messagesByChat: {
    [chatKey(INITIAL_CONVERSATION.hostId, "c-1")]: INITIAL_CONVERSATION.messages,
  },
  theme: "dark",
  accent: "#ff9344",
  navCollapsed: false,
  mobileView: "servers",

  setTheme: (t) => set({ theme: t }),
  setAccent: (c) => set({ accent: c }),
  toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
  setNavCollapsed: (collapsed) => set({ navCollapsed: collapsed }),
  setMobileView: (view) => set({ mobileView: view }),

  addHost: (payload) => {
    const newHost: Host = {
      id: "host-" + Date.now(),
      name: payload.label,
      addr: `${payload.user}@${payload.host}`,
      region: "us-east-1",
      tag: payload.tags[0] || "new",
      os: "ubuntu",
      connectionMode: "direct",
      status: "connecting",
      latency: 0,
      cpu: 0, mem: 0, disk: 0,
      lastSeen: "now",
      group: "",
      spark: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    };
    set((s) => ({ hosts: [...s.hosts, newHost] }));
    setTimeout(() => {
      set((s) => ({
        hosts: s.hosts.map((h) =>
          h.id === newHost.id
            ? { ...h, status: "online", latency: 32, cpu: 14, mem: 24, disk: 12, spark: [10, 12, 14, 12, 11, 13, 14, 12, 10, 11, 13, 14] }
            : h,
        ),
      }));
    }, 1400);
    return newHost.id;
  },

  addServerHost: (h) => {
    const local = serverToLocalHost(h);
    set((s) =>
      s.hosts.some((existing) => existing.id === h.id)
        ? s
        : { hosts: [...s.hosts, local] },
    );
  },

  setHosts: (hosts) => set({ hosts: hosts.map(serverToLocalHost) }),
  setHostsLoading: (loading) => set({ hostsLoading: loading }),

  updateHost: (id, patch) =>
    set((s) => ({
      hosts: s.hosts.map((h) => (h.id === id ? { ...h, ...patch } : h)),
    })),

  removeHost: (id) =>
    set((s) => ({
      hosts: s.hosts.filter((h) => h.id !== id),
    })),

  newChat: (hostId) => {
    const id = "c-" + Date.now();
    set((s) => ({
      chatsByHost: {
        ...s.chatsByHost,
        [hostId]: [
          { id, title: "New chat", time: "now", preview: "—", unread: 0 },
          ...(s.chatsByHost[hostId] || []),
        ],
      },
      messagesByChat: { ...s.messagesByChat, [chatKey(hostId, id)]: [] },
    }));
    return id;
  },

  approveTool: (key, msgId, toolId) => {
    set((s) => {
      const list = s.messagesByChat[key] || [];
      return {
        messagesByChat: {
          ...s.messagesByChat,
          [key]: list.map((m) =>
            m.id !== msgId
              ? m
              : {
                  ...m,
                  tools: m.tools?.map((t) =>
                    t.id === toolId ? { ...t, status: "running" as const, needsApproval: false } : t,
                  ),
                },
          ),
        },
      };
    });
    setTimeout(() => {
      set((s) => {
        const list = s.messagesByChat[key] || [];
        const updated = list.map((m) =>
          m.id !== msgId
            ? m
            : {
                ...m,
                tools: m.tools?.map((t) =>
                  t.id === toolId
                    ? {
                        ...t,
                        status: "success" as const,
                        output: [
                          { c: "mut", t: " pg_terminate_backend\n" },
                          { c: "mut", t: "----------------------\n" },
                          { c: "green", t: " t\n" },
                          { c: "mut", t: "(1 row)\n" },
                        ],
                      }
                    : t,
                ),
              },
        );
        return {
          messagesByChat: {
            ...s.messagesByChat,
            [key]: [
              ...updated,
              {
                id: "a4", role: "agent", time: "14:39",
                text: "Done — pid 39281 terminated. Replay lag dropped to **0.1s** within 3 seconds. I'm watching for 60s to confirm. Want me to add a guard rule that auto-cancels analytic queries on this replica after 5 minutes?",
              },
            ],
          },
        };
      });
    }, 900);
  },

  denyTool: (key, msgId, toolId) => {
    set((s) => {
      const list = s.messagesByChat[key] || [];
      return {
        messagesByChat: {
          ...s.messagesByChat,
          [key]: list.map((m) =>
            m.id !== msgId
              ? m
              : {
                  ...m,
                  tools: m.tools?.map((t) =>
                    t.id === toolId
                      ? {
                          ...t,
                          status: "error" as const,
                          needsApproval: false,
                          output: [{ c: "mut", t: "  denied by user — no action taken\n" }],
                        }
                      : t,
                  ),
                },
          ),
        },
      };
    });
  },

  sendMessage: (key, text) => {
    const userMsg: ChatMessage = {
      id: "u" + Date.now(),
      role: "user",
      time: "14:40",
      text,
    };
    set((s) => ({
      messagesByChat: {
        ...s.messagesByChat,
        [key]: [...(s.messagesByChat[key] || []), userMsg],
      },
    }));
    setTimeout(() => {
      set((s) => ({
        messagesByChat: {
          ...s.messagesByChat,
          [key]: [
            ...(s.messagesByChat[key] || []),
            {
              id: "a" + Date.now(),
              role: "agent",
              time: "14:40",
              text: "On it — let me check that.",
            },
          ],
        },
      }));
    }, 600);
  },
}), {
  name: "cotty-app-prefs",
  version: 1,
  // Only user preferences persist; hosts are server-derived (Zero sync) and
  // the chat data is mock — both must rebuild fresh each launch.
  partialize: (s) => ({
    theme: s.theme,
    accent: s.accent,
    navCollapsed: s.navCollapsed,
  }),
  migrate: (persisted) => persisted as AppStore,
}));
